import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ChevronDown, ArrowRight, Star, MapPin, Zap, Instagram, Youtube, Heart, Users, Shield, Ticket, Dumbbell } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_BADGE = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/aj8j6qqi_Main.png";
const LOGO_TEXT = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/i0g3lcc5_Bad%20Company%20Font.png";
const HERO_QUOTE = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/0t5s51m7_new%20hero%20image%20Bad%20Company.png";
const PORTRAIT = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/noinnlov_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2002_25_50%20PM.png";
const VIDEO_1 = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/n929jczx_WhatsApp%20Video%202569-03-26%20at%2010.38.46.mp4";
const QR_CODE = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/0ggqap4l_WhatsApp%20Image%202569-03-26%20at%2010.38.23.jpeg";

const SPONSORS = [
  { name: "Tuff N Uff", url: "https://tuffnuff.com" },
  { name: "Barry's Downtown Prime", url: "https://www.barrysdowntownprime.com" },
  { name: "Palms", url: "https://www.palms.com" },
  { name: "Ghostbar", url: "https://www.palms.com/nightlife/ghostbar" },
  { name: "MMA Weekly", url: "https://www.mmaweekly.com" },
  { name: "Goody's Popcorn", url: "https://goodyslv.com" },
  { name: "American Trophies & Awards", url: "https://www.americantrophieslv.com" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

export default function HomePage() {
  const [episodes, setEpisodes] = useState([]);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/episodes`).then(r => setEpisodes(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/sponsorship-tiers`).then(r => setTiers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ── HERO ── */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover bg-[#0a1628]" data-testid="hero-video">
            <source src="/hero_43s.mp4" type="video/mp4" />
          </video>
          <div className="hero-gradient absolute inset-0" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <img src={LOGO_BADGE} alt="Bad Company Veterans Foundation" className="h-28 sm:h-36 w-auto mx-auto mb-6" />
          </motion.div>
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1}>
            <p className="font-subheading text-xs sm:text-sm uppercase tracking-[0.3em] text-brand-gold mb-6">
              Veterans &bull; Purpose &bull; Experiences &bull; Community
            </p>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-heading text-4xl sm:text-5xl lg:text-[5rem] uppercase tracking-tighter leading-[0.9] mb-6">
            Connecting Veterans.<br/>Creating Opportunities.<br/>Changing Lives.
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={3}
            className="font-body text-base lg:text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Bad Company Veterans Foundation creates unforgettable experiences, builds community, and shares powerful stories from Las Vegas.
          </motion.p>
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4}
            className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/sponsor-veteran" data-testid="hero-cta-sponsor"
              className="font-subheading text-sm uppercase tracking-widest btn-gold px-8 py-3.5 flex items-center gap-2">
              <Heart size={16} /> Sponsor a Veteran
            </Link>
            <Link to="/get-involved" data-testid="hero-cta-get-involved"
              className="font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-8 py-3.5 hover:border-brand-gold hover:text-brand-gold transition-all duration-300">
              Become a Partner
            </Link>
            <Link to="/episodes" data-testid="hero-cta-episodes"
              className="font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-8 py-3.5 hover:border-white hover:bg-white/5 transition-all duration-300 flex items-center gap-2">
              <Play size={16} fill="white" /> Watch Episodes
            </Link>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator">
          <ChevronDown size={24} className="text-slate-500" />
        </div>
      </section>

      {/* ── 3 DIVISIONS ── */}
      <section className="py-20 lg:py-28 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Star, title: "Veteran Experiences", desc: "VIP events, concerts, UFC, celebrity meet-and-greets, and unforgettable Vegas experiences for veterans.", link: "/events", cta: "View Experiences", color: "border-brand-gold/30 hover:border-brand-gold" },
              { icon: Heart, title: "Sponsor a Veteran", desc: "Choose a veteran to sponsor directly. 100% of your contribution creates a life-changing experience.", link: "/sponsor-veteran", cta: "Sponsor Now", color: "border-brand-red/30 hover:border-brand-red" },
              { icon: Shield, title: "Veteran Resources", desc: "The ultimate veteran guide to Las Vegas — discounts, services, support, employment, and community.", link: "/resources", cta: "Browse Resources", color: "border-white/10 hover:border-white/30" },
            ].map((div, i) => (
              <motion.div key={div.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.6 }}>
                <Link to={div.link} className={`block border ${div.color} p-8 lg:p-10 card-hover group transition-all duration-400`}>
                  <div.icon size={28} className="text-brand-gold mb-5" />
                  <h3 className="font-subheading text-xl uppercase tracking-wider mb-3 group-hover:text-brand-gold transition-colors">{div.title}</h3>
                  <p className="font-body text-sm text-slate-500 mb-6 leading-relaxed">{div.desc}</p>
                  <span className="font-subheading text-xs uppercase tracking-widest text-brand-gold flex items-center gap-2">
                    {div.cta} <ArrowRight size={14} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HERO QUOTE IMAGE ── */}
      <section className="relative">
        <img src={HERO_QUOTE} alt="We've lived through things that should have stopped us. Instead, they led us here." className="w-full object-cover" data-testid="hero-quote-image" />
      </section>

      {/* ── FEATURED VIDEOS ── */}
      <section className="py-20 lg:py-28 bg-[#0f1f35]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-3">Watch Now</p>
              <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight">Bad Company In Action</h2>
            </div>
            <Link to="/episodes" className="hidden sm:flex items-center gap-2 font-subheading text-sm uppercase tracking-widest text-slate-400 hover:text-brand-gold transition-colors">
              View All <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { src: VIDEO_1, poster: "/thumb_v1.jpg", title: "Bad Company Experience" },
              { src: "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/coit6ry4_WhatsApp%20Video%202569-03-26%20at%2011.29.12.mp4", poster: "/thumb_v4.jpg", title: "Fight Night" },
              { src: "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/jv7kyw57_WhatsApp%20Video%202569-03-26%20at%2011.44.48.mp4", poster: "/thumb_v5.jpg", title: "Vegas Nights" },
            ].map((vid, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border border-white/10 overflow-hidden card-hover">
                <video controls preload="metadata" className="w-full aspect-video object-cover bg-[#0a1628]" poster={vid.poster}>
                  <source src={vid.src} type="video/mp4" />
                </video>
                <div className="p-4">
                  <h3 className="font-subheading text-sm uppercase tracking-wider">{vid.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT PREVIEW ── */}
      <section className="py-20 lg:py-28 bg-[#0a1628] noise-overlay relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-4">Who We Are</p>
              <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-tight mb-6">
                More Than Entertainment
              </h2>
              <p className="font-body text-base lg:text-lg text-slate-400 leading-relaxed mb-8">
                Bad Company Veterans Foundation is a veteran-focused content and experience platform that combines storytelling, entertainment, and real-world impact. Based in Las Vegas, we create unforgettable moments and share powerful stories with the world.
              </p>
              <Link to="/about" data-testid="about-preview-cta"
                className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest text-brand-gold border-b border-brand-gold/30 pb-1 hover:border-brand-gold transition-colors duration-300">
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
              <div className="overflow-hidden border border-white/10">
                <img src={PORTRAIT} alt="Bad Company crew" className="w-full aspect-[4/5] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0a1628] border border-brand-gold/30 p-5">
                <img src={LOGO_BADGE} alt="Bad Company" className="h-16 w-16 object-contain" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── MISSION VIDEO ── */}
      <section className="py-20 lg:py-28 relative overflow-hidden" style={{minHeight: '500px'}}>
        <div className="absolute inset-0">
          <video autoPlay loop muted playsInline preload="auto"
            poster="https://static.prod-images.emergentagent.com/jobs/02c52b1a-551f-4ab4-bb4f-fdecb1df21b7/images/a0af1d125aa03397845c84f2eb1032168b36a0abfab58717dba600a82c5b4921.png"
            className="w-full h-full object-cover" data-testid="mission-bg-video">
            <source src="/mission_bg.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#0a1628]/60" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-4">Our Mission</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.9] mb-8">
              Purpose Beyond<br/>Entertainment
            </h2>
            <p className="font-body text-base lg:text-lg text-slate-300 leading-relaxed mb-10 max-w-2xl mx-auto">
              Our mission is to provide veterans with unforgettable, once-in-a-lifetime experiences while capturing and sharing their stories to inspire others.
            </p>
            <Link to="/mission" className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest btn-gold px-8 py-3.5">
              Our Mission <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── FITNESS CAMP PROMO ── */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1607962837359-5e7e89f86776?crop=entropy&cs=srgb&fm=jpg&w=1600" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#0a1628]/60" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-brand-gold border border-brand-gold/30 px-4 py-1 inline-block mb-6">New Program</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.9] mb-6">Veteran Fitness Camp</h2>
            <p className="font-body text-base lg:text-lg text-slate-300 leading-relaxed mb-4 max-w-2xl mx-auto">
              Transform your body and mind. Discounted training programs with elite trainers, nutrition plans, and team building.
            </p>
            <p className="font-subheading text-lg uppercase tracking-wider text-brand-gold mb-8">3 Free Spots Available</p>
            <Link to="/fitness-camp" className="font-subheading text-sm uppercase tracking-widest btn-gold px-8 py-3.5 inline-flex items-center gap-2">
              Apply Now <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── SPONSORS ── */}
      <section className="py-20 lg:py-28 bg-[#0f1f35]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-3">Our Partners</p>
            <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight mb-3">Thank You To Our Sponsors</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-12">
            {SPONSORS.map((s, i) => (
              <motion.a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.4 }}
                className="border border-white/10 p-4 flex items-center justify-center text-center card-hover min-h-[80px] hover:border-brand-gold/30">
                <span className="font-subheading text-xs uppercase tracking-wider text-slate-400 hover:text-white transition-colors">{s.name}</span>
              </motion.a>
            ))}
          </div>
          {/* Goody's Partnership */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="border border-brand-gold/20 overflow-hidden max-w-3xl mx-auto p-8 sm:p-10 text-center">
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-brand-gold border border-brand-gold/30 px-3 py-1 inline-block mb-4">Official Partner</span>
            <h3 className="font-subheading text-2xl uppercase tracking-tight mb-3">Goody's Popcorn</h3>
            <p className="font-body text-lg text-white mb-2">Use promo code <span className="bg-brand-gold text-[#0a1628] px-3 py-1 font-semibold">BADCOMPANY</span></p>
            <p className="font-body text-sm text-slate-500 mb-6">50% of all proceeds go directly to supporting our veterans</p>
            <a href="https://goodyslv.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest btn-gold px-8 py-3">
              Order Now <ArrowRight size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── SUPPORT / QR ── */}
      <section className="py-20 lg:py-28 bg-[#0a1628] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.9] mb-4">Support Our Veterans</h2>
          <p className="font-body text-base text-slate-400 mb-10">Help us continue to give back to those who served</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="border border-white/10 p-8 text-center card-hover">
              <Youtube size={28} className="text-brand-gold mx-auto mb-4" />
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-4">Subscribe on YouTube</h3>
              <a href="https://youtube.com/@BadCompanyEntertainmentTV" target="_blank" rel="noopener noreferrer"
                className="inline-block font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-brand-gold transition-all">
                Watch on YouTube
              </a>
            </div>
            <div className="border border-white/10 p-8 text-center card-hover">
              <Heart size={28} className="text-brand-gold mx-auto mb-4" />
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-4">Donate on GoFundMe</h3>
              <img src={QR_CODE} alt="GoFundMe QR" className="w-32 h-32 mx-auto mb-4 bg-white p-1" />
              <p className="font-body text-xs text-slate-500">Scan to donate</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── INSTAGRAM ── */}
      <section className="py-20 lg:py-28 bg-[#0f1f35] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Instagram size={20} className="text-brand-gold" />
            <p className="font-body text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Follow Along</p>
          </div>
          <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight mb-8">@BadCompany_Entertainment</h2>
          <a href="https://instagram.com/BadCompany_Entertainment" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-8 py-3.5 hover:border-brand-gold transition-all mb-12">
            <Instagram size={16} /> Follow on Instagram
          </a>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 lg:py-28 bg-[#0a1628] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <img src={LOGO_BADGE} alt="Bad Company" className="h-20 w-20 mx-auto mb-6 opacity-30" />
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.9] mb-6">
              Ready To Make<br/>An Impact?
            </h2>
            <p className="font-body text-base text-slate-500 mb-10 max-w-lg mx-auto">
              Whether you want to nominate a veteran, become a sponsor, or simply connect with our mission.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/get-involved" className="font-subheading text-sm uppercase tracking-widest btn-gold px-8 py-3.5">
                Nominate a Veteran
              </Link>
              <Link to="/get-involved" className="font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-8 py-3.5 hover:border-brand-gold transition-all">
                Become a Sponsor
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
